import { PUSH_SERVICE_WORKER_PATH } from '@/lib/constants';
import { logger } from '@/lib/logger';

/** The message public/sw.js posts to a window whose reload the browser refused. */
export const TOMBSTONE_NAVIGATE_REFUSED = 'iskool-tombstone-navigate-refused';

// DOMException names a browser gives when it blocks the Service Worker or Cache
// Storage API (site data blocked, some private modes) or the page is not fully
// active: an environment, not an app error.
const BLOCKED_API_ERRORS = new Set(['SecurityError', 'InvalidStateError']);

/** Whether a worker script is the push worker (PUSH_SERVICE_WORKER_PATH). */
export function isPushWorkerScript(scriptURL: string | undefined): boolean {
  return PUSH_SERVICE_WORKER_PATH !== null && scriptURL !== undefined
    && new URL(scriptURL).pathname === PUSH_SERVICE_WORKER_PATH;
}

/** The script a registration runs: its active worker's, else the one waiting or installing. */
function scriptOf(reg: ServiceWorkerRegistration | undefined): string | undefined {
  return (reg?.active ?? reg?.waiting ?? reg?.installing)?.scriptURL;
}

/**
 * Reports a failed Service Worker or Cache Storage lookup. A browser that blocks
 * these APIs goes to the sampled metric stream, not to Sentry errors; any other
 * failure is an error.
 */
export function reportLookupFailure(metric: string, err: unknown): void {
  // `name` read structurally: a DOMException from another realm fails instanceof Error.
  const name = typeof err === 'object' && err !== null && 'name' in err ? String(err.name) : undefined;
  if (name !== undefined && BLOCKED_API_ERRORS.has(name)) {
    logger.metric(metric, { error: name });
  } else {
    logger.error(metric, err);
  }
}

/**
 * 00b R17: the app registers no service worker and never uses Cache Storage,
 * so any worker or cache a page finds at boot, other than the push worker once
 * it ships, is left over from before R17 (the v2.3 caching worker or the kill
 * switch) or from the /sw.js tombstone curing it. Reporting them measures how
 * many browsers still need the tombstone, so Q-05 can retire it on evidence, and
 * shows what the tombstone cannot report itself: a page it left controlled, or
 * caches it could not delete.
 *
 * Each such browser reports about once, because the tombstone cures it on that
 * visit, so the report is sent unsampled. The count is a lower bound: a page the
 * tombstone reloads before the send completes loses it. Like every
 * logger.metric call it reaches Sentry from the prod tier only (dev logs it to
 * the console; staging's build drops console calls). It cannot see a browser
 * whose old registration sits on an origin that redirects /sw.js (today the
 * apex iskool.ai): the update checks there never reach the tombstone.
 */
export async function reportLegacyServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const [registrations, cacheNames] = await Promise.all([
      navigator.serviceWorker.getRegistrations(),
      'caches' in window ? caches.keys() : Promise.resolve([]),
    ]);
    const leftovers = registrations.filter((reg) => !isPushWorkerScript(scriptOf(reg)));
    const controller = navigator.serviceWorker.controller;
    const controlledByLeftover = controller !== null && !isPushWorkerScript(controller.scriptURL);
    if (!controlledByLeftover && leftovers.length === 0 && cacheNames.length === 0) return;

    logger.metric('legacy-sw-present', {
      controlled: controlledByLeftover,
      scriptURL: (controlledByLeftover ? controller?.scriptURL : scriptOf(leftovers[0])) ?? null,
      registrations: leftovers.length,
      caches: cacheNames.length,
    }, { sampled: false });
  } catch (err) {
    reportLookupFailure('legacy-sw-lookup-failed', err);
  }
}

/**
 * Reports a window the /sw.js tombstone could not reload. The browser can refuse
 * the tombstone's navigate(); the tombstone then posts TOMBSTONE_NAVIGATE_REFUSED
 * to that window, which keeps its page and never boots again under the old
 * worker, so only this listener can report it. Returns the cleanup that removes
 * the listener.
 */
export function reportTombstoneRefusals(): () => void {
  if (!('serviceWorker' in navigator)) return () => {};
  const onMessage = (event: MessageEvent) => {
    if ((event.data as { type?: unknown } | null)?.type !== TOMBSTONE_NAVIGATE_REFUSED) return;
    logger.metric('tombstone-navigate-refused', { visibility: document.visibilityState }, { sampled: false });
  };
  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
}
